import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import register from "../../services/PowerBi";
import { toast, ToastContainer } from "react-toastify";
import UserComponent, { userBackObj } from "./UserComponent";
import Users from "../../services/User";
import AttToken from "../../services/AttToken";
import { Button, Skeleton, Stack, TextField, Typography } from "@mui/material";
import Empresas from "../../services/Empresas";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import Autocomplete from "@mui/material/Autocomplete";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import PowerBiNode from "../../services/PowerBiNode";
import { empresaType } from "../../context/EmpresasContext";

const empresaService = new Empresas();
const usersService = new Users();
const servicePowerBi = new register();
const servicePowerBiNode = new PowerBiNode();

export type UserAccessType = {
  [userName: string]: string[];
};

export type empresaReportsType = {
  [id: string]: singleEmpresaType;
};

export type singleEmpresaType = {
  datasetId: string;
  datasetWorkspaceId: string;
  id: string;
  name: string;
  users: empresaReportsType;
};

export default function DashboardComponent() {
  const [url, setUrl] = useState<string>("");
  const [empresa, setEmpresa] = useState<string>("");
  const [selectedReport, setSelectedReport] = useState<singleEmpresaType | null>(null);
  const [usersAdded, setUsersAdded] = useState<userBackObj[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string>("");
  const [userReports, setUserReports] = useState<singleEmpresaType[]>([]);
  const [empresaNome, setEmpresaNome] = useState<string>("");
  const [updates, setUpdates] = useState<any>({});

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ["GET_EMPRESAS"],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const response = await empresaService.getAll(token);
        return response.empresas;
      }
    },
    keepPreviousData: true,
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  const { data: users } = useQuery({
    queryKey: ["GET_USERS", empresa],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const body = { ...token };
        const result = await usersService.getAllByCompany(body, empresa as string);
        return result;
      }
    },
    staleTime: 5 * 60 * 1000,
    enabled: empresa?.length > 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  if (isError) {
    toast.error("Erro ao buscar empresas");
  }

  const {
    data: empresaReports,
    isLoading: loadingReports,
    isFetching: fetchingReports,
    isSuccess,
    refetch,
    isError: isErrorOnReports,
  } = useQuery({
    queryKey: ["GET_EMPRESA_REPORTS", empresa],
    queryFn: async () => {
      toast.info(`Buscando workspace da empresa ${empresaNome}...`);
      const token = await AttToken();
      if (token) {
        const response = await servicePowerBiNode.getEmpresaReports(token, empresa);
        return response;
      }
    },
    staleTime: 5 * 60 * 1000,
    enabled: empresa?.length > 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  function convertApiResponseToArray(apiResponse: any) {
    setWorkspaceId(Object.keys(apiResponse)[0]);
    let allDatasets: any[] = [];
    for (const workspaceId in apiResponse) {
      if (apiResponse.hasOwnProperty(workspaceId)) {
        const workspace = apiResponse[workspaceId];
        const datasets = Object.values(workspace);
        allDatasets = allDatasets.concat(datasets);
      }
    }
    return setUserReports(allDatasets);
  }

  useEffect(() => {
    if (isSuccess && empresaReports.reports === null) {
      toast.info(`Nenhum workspace cadastrado para a empresa ${empresaNome}`);
    }
    if (isSuccess && empresaReports.reports) {
      toast.success(`Workspace da empresa ${empresaNome} encontrado`);
      convertApiResponseToArray(empresaReports.reports);
    }
    if (isErrorOnReports) {
      toast.error(`Erro ao buscar workspace da empresa ${empresaNome}`);
    }
  }, [empresaReports, isSuccess, isErrorOnReports, empresaNome]);

  useEffect(() => {
    if (userReports.length) {
      for (const reports of userReports) {
        const res = `${reports.id}/users`;
        setUpdates((prev: any) => ({ ...prev, [res]: reports.users }));
      }
    }
  }, [userReports]);

  const { mutate: registerWorkSpace, isLoading: loadingRegister } = useMutation({
    mutationKey: ["REGISTER_WORKSPACE"],
    mutationFn: async () => {
      toast.warn(`Salvando workspace para empresa ${empresaNome}...`);
      const token = await AttToken();
      if (token) {
        const response = await servicePowerBiNode.registerWorkSpace(token, empresa, url);
        return response;
      }
    },
    onSuccess: (data) => {
      if (data) {
        refetch();
        setUrl(``);
        toast.success(`Workspace salvo para empresa ${empresaNome}`);
      }
    },
    onError: (_error) => {
      toast.error("Erro ao cadastrar workspace da empresa " + empresaNome);
    },
  });

  const { mutate: manageLicenses } = useMutation({
    mutationKey: ["MANAGE_LICENSES"],
    mutationFn: async () => {
      toast.warn(`Salvando alterações empresa ${empresaNome}...`);
      const token = await AttToken();
      if (token) {
        const update = { [workspaceId]: updates };
        const response = await servicePowerBiNode.manageLicenses(token, empresa, update);
        return response;
      }
    },
    onSuccess: () => {
      toast.success(`atualizações salvas, atualizando workspace empresa ${empresaNome}...`);
      updateWorkspace();
    },
    onError: () => {
      toast.error(`Erro ao atualizar workspace empresa ${empresaNome}`);
    },
  });

  const { mutate: updateWorkspace } = useMutation({
    mutationKey: ["UPDATE_WORKSPACE"],
    mutationFn: async () => {
      toast.info(`Atualizando workspace empresa ${empresaNome}...`);
      const token = await AttToken();
      if (token) {
        const response = await servicePowerBiNode.updateWorkspace(token, empresa, workspaceId);
        return response;
      }
    },
    onSuccess: () => {
      refetch();
      toast.success(`Atualizado com sucesso o workspace da empresa ${empresaNome}!`);
    },
    onError: () => {
      toast.error(`Erro ao atualizar workspace empresa ${empresaNome}`);
    },
  });

  const { mutate: removeDashBoard, isLoading: loadingRemove } = useMutation({
    mutationKey: ["REMOVE_DASHBOARD"],
    mutationFn: async () => {
      toast.warn(`Removendo dashboard da empresa ${empresaNome}...`);
      const token = await AttToken();
      if (token) {
        await servicePowerBi.deleteWorkspace(token.userToken, token.username, token.userEmpresa, empresa);
      }
    },
    onSuccess: () => {
      refetch();
      setSelectedReport(null);
      setUserReports([]);
      setUrl(``);
      toast.error(`Dashboard removido para empresa ${empresaNome}!`);
    },
    onError: (error) => {
      toast.error("Erro ao remover dashboard da empresa" + empresaNome);
    },
  });

  const renderSwitch = (value: string) => {
    switch (String(value)) {
      case "rls_gerente":
        return "Gerente";
      case "rls_cliente":
        return "Cliente";
      case "rls_conjunto":
        return "Conjunto";

      case "rls_empresa":
        return "Empresa";
      default:
        return "Todos";
    }
  };

  const columns: GridColDef[] = [
    { field: "nome", headerName: "Nome", width: 250 },
    { field: "email", headerName: "E-mail", width: 250 },
    { field: "id", headerName: "User Name", width: 150 },
    { field: "role", headerName: "Perfil", width: 120 },
    {
      field: "matricula",
      headerName: "Função",
      width: 100,
      valueFormatter: (params) => {
        console.log(params);
        return renderSwitch(params);
      },
    },
    {
      field: "remove",
      headerName: "Remover",
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          color="error"
          endIcon={<DeleteIcon />}
          onClick={() => {
            setUsersAdded(usersAdded?.filter((e: userBackObj) => e.id !== params.id));
            const usuarios = userReports?.find((report) => report.id === selectedReport?.id)?.users;
            delete usuarios?.[params.id as string];
            delete updates[`${selectedReport?.id}/users${params.id as string}`];
          }}>
          Remover
        </Button>
      ),
    },
  ];

  function handleUrl(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setUrl(event.target.value);
  }

  const onLoading = loadingReports || fetchingReports || loadingRegister;

  return (
    <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" width="100%" spacing={2}>
      <ToastContainer />
      <Typography variant="h5" fontWeight={600} color="textDisabled" textAlign="center" width="100%">
        Dashboards
      </Typography>
      <Autocomplete
        fullWidth
        getOptionLabel={(option) => option.nome as string}
        getOptionKey={(option) => option.id as string}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={isLoading || isFetching}
        loadingText="Buscando empresas..."
        disabled={isLoading || isFetching}
        noOptionsText={"Não encontrou empresas"}
        options={
          data
            ?.sort((a: empresaType, b: empresaType) => {
              return a?.nome?.toLowerCase() > b?.nome?.toLowerCase() ? 1 : -1;
            })
            ?.filter((elem: empresaType) => elem?.status === "ativo")
            ?.map((empresa: empresaType) => ({
              ...empresa,
            })) || []
        }
        onChange={async (_event: any, newValue: empresaType | null) => {
          setEmpresa(newValue?.id as string);
          setEmpresaNome(newValue?.nome as string);
          setUsersAdded([]);
          setUpdates([]);
          setUserReports([]);
          setSelectedReport(null);
        }}
        renderInput={(params) => <TextField {...params} label={"Selecione a empresa"} />}
        renderOption={(props, option: any) => (
          <li {...props} key={option.id}>
            <span>{option.nome}</span>
          </li>
        )}
      />
      <React.Fragment>
        {onLoading ? (
          <Skeleton variant="rounded" height={60} width="100%" />
        ) : (
          <React.Fragment>
            {!empresaReports?.reports && empresa ? (
              <form style={{ width: "100%" }}>
                <Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%" spacing={0.5}>
                  <TextField fullWidth label="Insira uma URL para cadastrar:" value={url} onChange={handleUrl} />
                  <Button
                    endIcon={<SaveIcon />}
                    variant="contained"
                    onClick={(e) => {
                      e.preventDefault();
                      registerWorkSpace();
                    }}
                    disabled={!url.length}
                    type="submit">
                    Salvar
                  </Button>
                </Stack>
              </form>
            ) : (
              userReports.length > 0 &&
              empresa && (
                <React.Fragment>
                  <Autocomplete
                    getOptionLabel={(option) => option.name as string}
                    getOptionKey={(option) => option.id as string}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    fullWidth
                    noOptionsText={"Sem dashboards"}
                    disabled={!empresaReports}
                    options={
                      userReports
                        ?.sort((a: any, b: any) => {
                          return a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1;
                        })
                        ?.map((report: any) => ({
                          ...report,
                        })) ?? []
                    }
                    onChange={(_event: any, newValue: singleEmpresaType | null) => {
                      setSelectedReport(newValue);
                      if (!newValue) {
                        setUsersAdded([]);
                        return;
                      }
                      const usersAddedList = Object.keys(newValue.users).reduce((acc: any[], userId) => {
                        const user = users?.find((u: userBackObj) => u.id === userId);

                        if (!user) return acc;

                        const matricula = newValue.users[userId];

                        acc.push({
                          ...user,
                          matricula: matricula,
                        });

                        return acc;
                      }, []);

                      setUsersAdded(usersAddedList);
                    }}
                    renderInput={(params) => <TextField {...params} label={"Selecione a dashboard"} />}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <span>{option.name}</span>
                      </li>
                    )}
                  />
                  <Stack direction="row" alignItems="flex-start" justifyContent="flex-start" spacing={1}>
                    <Button
                      variant="contained"
                      color="success"
                      endIcon={<SaveIcon />}
                      disabled={!usersAdded?.length || !selectedReport?.name}
                      onClick={() => manageLicenses()}>
                      Salvar alterações
                    </Button>
                    <Button
                      endIcon={<DeleteIcon />}
                      color="error"
                      variant="contained"
                      disabled={loadingRemove || loadingReports}
                      onClick={() => removeDashBoard()}>
                      Remover workspace
                    </Button>
                  </Stack>
                </React.Fragment>
              )
            )}
          </React.Fragment>
        )}
        {selectedReport && (
          <React.Fragment>
            {usersAdded && (
              <>
                <Typography color="textDisabled" fontSize="medium">
                  Usuários adicionados
                </Typography>
                <DataGrid
                  autoHeight
                  sx={{ "--css-1jlz3st": "display:  none", width: "100%" }}
                  getRowHeight={() => "auto"}
                  getRowId={(row: any) => row.id}
                  editMode="row"
                  rows={usersAdded}
                  columns={columns}
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 10 },
                    },
                  }}
                  pageSizeOptions={[10, 25]}
                  localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
                />
              </>
            )}
            {users && (
              <>
                <Typography color="textDisabled" fontSize="medium">
                  Usuários para adicionar
                </Typography>
                <UserComponent
                  setUserReports={setUserReports}
                  usersAdded={usersAdded}
                  setUsersAdded={setUsersAdded}
                  selectedReport={selectedReport}
                  userData={
                    users?.sort((a: userBackObj, b: userBackObj) => {
                      return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1;
                    }) as userBackObj[]
                  }
                />
              </>
            )}
          </React.Fragment>
        )}
      </React.Fragment>
    </Stack>
  );
}
